'use strict';
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';

// ============================================================
// Styles
// ============================================================
const inputStyle = {
  padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
  fontSize: '14px', width: '100%', boxSizing: 'border-box'
};
const btnStyle = {
  padding: '10px 18px', background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
  color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px',
  fontWeight: '600', cursor: 'pointer'
};
const cardStyle = {
  border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: 'white'
};

function Msg({ msg }) {
  if (!msg) return null;
  return <p style={{ color: msg.ok ? '#16a34a' : '#dc2626', margin: '8px 0', fontSize: '14px' }}>{msg.text}</p>;
}

function TrafficLight({ value, good, warn }) {
  const color = value === undefined || value === null ? '#94a3b8' : value <= good ? '#22c55e' : value <= warn ? '#f59e0b' : '#ef4444';
  return <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: color, marginRight: 6, verticalAlign: 'middle' }} />;
}

// ============================================================
// Tab: Systemübersicht
// ============================================================
function UebersichtTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/v2/system/overview')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p>Lade Systemübersicht…</p>;
  if (!data) return <p style={{ color: '#ef4444' }}>Systemübersicht nicht verfügbar.</p>;

  const indicators = data.indicators || {};
  const db = data.database || {};
  const app = data.app || {};

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Systemübersicht</h2>
        <button onClick={load} style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', background: '#f8fafc' }}>Aktualisieren</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Buchungen', value: indicators.buchungen_total, unit: '', color: '#6366f1' },
          { label: 'Kategorien', value: indicators.kategorien_total, unit: '', color: '#10b981' },
          { label: 'Regeln', value: indicators.regeln_total, unit: '', color: '#3b82f6' },
          { label: 'DB-Größe', value: indicators.db_size_kb ? `${Math.round(indicators.db_size_kb)} KB` : '-', unit: '', color: '#f59e0b', raw: true },
          { label: 'Uptime', value: indicators.uptime_minutes ? `${Math.round(indicators.uptime_minutes)} min` : '-', unit: '', color: '#8b5cf6', raw: true },
          { label: 'Benutzer', value: indicators.users_total, unit: '', color: '#06b6d4' },
        ].map(({ label, value, color, raw }) => (
          <div key={label} style={{ ...cardStyle, textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: '700', color }}>{raw ? value : (value ?? '-')}</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Datenbank</h3>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['WAL-Modus', db.wal_mode ? 'Aktiv' : 'Inaktiv'],
                ['Foreign Keys', db.foreign_keys ? 'Aktiv' : 'Inaktiv'],
                ['Integrity', db.integrity_ok ? '✓ OK' : '✗ Fehler'],
                ['Größe', db.size_kb ? `${Math.round(db.size_kb)} KB` : '-'],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={{ color: '#64748b', paddingBottom: '6px' }}>{k}</td>
                  <td style={{ fontWeight: '600', paddingBottom: '6px' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Anwendung</h3>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Version', app.version || data.version || '-'],
                ['Node', app.node_version || '-'],
                ['Platform', app.platform || '-'],
                ['Started', data.started_at ? new Date(data.started_at).toLocaleString('de-DE') : '-'],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={{ color: '#64748b', paddingBottom: '6px' }}>{k}</td>
                  <td style={{ fontWeight: '600', paddingBottom: '6px' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab: Benutzerverwaltung
// ============================================================
function BenutzerTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ username: '', email: '', role: 'leser' });
  const [roles, setRoles] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/admin/v2/users'),
      api.get('/admin/v2/roles'),
    ]).then(([u, r]) => {
      setUsers(u.data.users || []);
      setRoles(r.data.roles || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function createUser(e) {
    e.preventDefault();
    setMsg(null);
    try {
      await api.post('/admin/v2/users', form);
      setMsg({ ok: true, text: 'Benutzer erstellt. Temporäres Passwort liegt in /data/secrets/' });
      setForm({ username: '', email: '', role: 'leser' });
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Fehler beim Erstellen' });
    }
  }

  async function toggleUser(id, aktiv) {
    try {
      await api.put(`/admin/v2/users/${id}`, { is_active: aktiv ? 0 : 1 });
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Fehler' });
    }
  }

  async function resetPassword(id, username) {
    if (!window.confirm(`Passwort für "${username}" zurücksetzen?`)) return;
    try {
      await api.post(`/admin/v2/users/${id}/reset-password`);
      setMsg({ ok: true, text: 'Passwort zurückgesetzt. Neues Passwort in /data/secrets/' });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Fehler' });
    }
  }

  if (loading) return <p>Lade Benutzer…</p>;

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>Benutzerverwaltung</h2>
      <Msg msg={msg} />

      <section style={{ ...cardStyle, marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Neuer Benutzer</h3>
        <form onSubmit={createUser} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: '8px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Benutzername</label>
            <input style={inputStyle} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required minLength={3} />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>E-Mail (optional)</label>
            <input type="email" style={inputStyle} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Rolle</label>
            <select style={{ ...inputStyle, width: 'auto' }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              {roles.map(r => <option key={r.slug} value={r.slug}>{r.display_name}</option>)}
            </select>
          </div>
          <button type="submit" style={{ ...btnStyle, padding: '10px 16px' }}>Erstellen</button>
        </form>
      </section>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {users.map(u => (
          <div key={u.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#6366f1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '16px', flexShrink: 0 }}>
              {(u.username || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>
                {u.username}
                {!u.is_active && <span style={{ marginLeft: '8px', fontSize: '11px', background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px' }}>Gesperrt</span>}
                {u.force_password_change ? <span style={{ marginLeft: '6px', fontSize: '11px', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px' }}>PW ändern</span> : null}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {u.email || '-'} · Rollen: {u.roles || 'keine'}
                {u.last_login_at ? ` · Login: ${new Date(u.last_login_at).toLocaleString('de-DE')}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => resetPassword(u.id, u.username)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', background: '#f8fafc' }}>PW-Reset</button>
              {u.username !== 'admin' && (
                <button onClick={() => toggleUser(u.id, u.is_active)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', background: u.is_active ? '#fee2e2' : '#dcfce7', color: u.is_active ? '#dc2626' : '#16a34a' }}>
                  {u.is_active ? 'Sperren' : 'Entsperren'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Tab: Rollen & Berechtigungen
// ============================================================
function RollenTab() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rolePerms, setRolePerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get('/admin/v2/roles'), api.get('/admin/v2/permissions')])
      .then(([r, p]) => { setRoles(r.data.roles || []); setPerms(p.data.permissions || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function selectRole(role) {
    setSelected(role);
    setRolePerms(role.permissions || []);
  }

  async function saveRolePerms() {
    if (!selected) return;
    try {
      await api.put(`/admin/v2/roles/${selected.id}/permissions`, { permissions: rolePerms });
      setMsg({ ok: true, text: 'Berechtigungen gespeichert' });
      load();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Fehler' });
    }
  }

  if (loading) return <p>Lade Rollen…</p>;

  const categories = [...new Set(perms.map(p => p.category))];

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>Rollen & Berechtigungen</h2>
      <Msg msg={msg} />
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '16px' }}>
        <div>
          <h3 style={{ fontSize: '14px', marginBottom: '8px' }}>Rollen</h3>
          {roles.map(r => (
            <button key={r.id} onClick={() => selectRole(r)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: '4px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: selected?.id === r.id ? '#eff6ff' : 'white', fontWeight: selected?.id === r.id ? '700' : '400', fontSize: '14px' }}>
              {r.display_name}
              {r.is_system ? <span style={{ marginLeft: '6px', fontSize: '11px', color: '#94a3b8' }}>System</span> : null}
            </button>
          ))}
        </div>
        <div>
          {selected ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '14px', margin: 0 }}>Berechtigungen: {selected.display_name}</h3>
                {!selected.is_system && <button onClick={saveRolePerms} style={btnStyle}>Speichern</button>}
              </div>
              {categories.map(cat => (
                <div key={cat} style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '13px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>{cat}</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px' }}>
                    {perms.filter(p => p.category === cat).map(p => (
                      <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: '#f8fafc', borderRadius: '6px', fontSize: '13px', cursor: selected.is_system ? 'default' : 'pointer' }}>
                        <input type="checkbox" checked={rolePerms.includes(p.key)} disabled={selected.is_system}
                          onChange={e => {
                            if (e.target.checked) setRolePerms(prev => [...prev, p.key]);
                            else setRolePerms(prev => prev.filter(k => k !== p.key));
                          }}
                        />
                        {p.display_name}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : <p style={{ color: '#94a3b8' }}>Rolle auswählen</p>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab: Sicherheit (bestehend)
// ============================================================
function SicherheitTab({ firstLogin }) {
  const [altes, setAltes] = useState('');
  const [neues, setNeues] = useState('');
  const [neues2, setNeues2] = useState('');
  const [msg, setMsg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/auth/sessions').then(r => setSessions(r.data.sessions || [])).catch(() => {});
  }, []);

  async function changePassword(e) {
    e.preventDefault();
    setMsg(null);
    if (neues !== neues2) { setMsg({ ok: false, text: 'Passwörter stimmen nicht überein' }); return; }
    if (neues.length < 12) { setMsg({ ok: false, text: 'Mindestens 12 Zeichen erforderlich' }); return; }
    if (!/[A-Z]/.test(neues) || !/[a-z]/.test(neues) || !/[0-9]/.test(neues) || !/[!@#$%^&*()\-_=+[\]{};:,.<>?|]/.test(neues)) {
      setMsg({ ok: false, text: 'Groß-/Kleinbuchstaben, Zahl und Sonderzeichen erforderlich' }); return;
    }
    setLoading(true);
    try {
      await api.post('/admin/auth/change-password', { altes_passwort: altes, neues_passwort: neues });
      setMsg({ ok: true, text: 'Passwort geändert. Bitte erneut anmelden...' });
      setAltes(''); setNeues(''); setNeues2('');
      setTimeout(() => {
        api.post('/logout').catch(() => {});
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Fehler beim Ändern' });
    } finally { setLoading(false); }
  }

  async function revokeSession(id) {
    try {
      await api.delete(`/admin/auth/sessions/${id}`);
      setSessions(s => s.filter(x => x.id !== id));
    } catch {}
  }

  const activeSessions = sessions.filter(s => !s.revoked_at && new Date(s.expires_at) > new Date());

  return (
    <div>
      {firstLogin && <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', color: '#92400e' }}><strong>Erstanmeldung:</strong> Bitte ändere dein Passwort.</div>}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ marginBottom: '16px' }}>Passwort ändern</h2>
        <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '360px' }}>
          {!firstLogin && <input type="password" placeholder="Aktuelles Passwort" value={altes} onChange={e => setAltes(e.target.value)} style={inputStyle} required />}
          <input type="password" placeholder="Neues Passwort (min. 12 Zeichen)" value={neues} onChange={e => setNeues(e.target.value)} style={inputStyle} required minLength={12} />
          <input type="password" placeholder="Neues Passwort bestätigen" value={neues2} onChange={e => setNeues2(e.target.value)} style={inputStyle} required />
          <Msg msg={msg} />
          <button type="submit" disabled={loading} style={btnStyle}>{loading ? 'Speichern…' : 'Passwort speichern'}</button>
        </form>
      </section>
      <section>
        <h2 style={{ marginBottom: '16px' }}>Aktive Sessions ({activeSessions.length})</h2>
        {activeSessions.length === 0 && <p style={{ color: '#64748b' }}>Keine aktiven Sessions.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {activeSessions.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', background: s.is_current ? '#f0fdf4' : 'white' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', color: '#64748b' }}>{s.is_current ? <strong style={{ color: '#16a34a' }}>Aktuelle Session · </strong> : ''}Erstellt: {new Date(s.created_at).toLocaleString('de-DE')}</div>
                <div style={{ fontSize: '12px', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.ip} · {(s.user_agent || '').slice(0, 60)}</div>
              </div>
              {!s.is_current && <button onClick={() => revokeSession(s.id)} style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Beenden</button>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// Tab: KI-Anbieter (bestehend)
// ============================================================
function ProviderCard({ provider: p, onToggle, onSaveKey }) {
  const [keyInput, setKeyInput] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', background: p.aktiv ? 'white' : '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <strong>{p.name}</strong>
          {p.ist_aktiver_provider ? <span style={{ marginLeft: '8px', background: '#dcfce7', color: '#16a34a', fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}>Aktiv</span> : null}
          {p.api_key_vorhanden ? <span style={{ marginLeft: '8px', background: '#eff6ff', color: '#3b82f6', fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}>Key gespeichert</span> : null}
        </div>
        <button onClick={() => onToggle(p.id, p.aktiv)} style={{ padding: '6px 12px', background: p.aktiv ? '#dcfce7' : '#f1f5f9', color: p.aktiv ? '#16a34a' : '#64748b', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>{p.aktiv ? 'Aktiv' : 'Inaktiv'}</button>
      </div>
      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>{p.base_url} {p.erfordert_key ? '· API-Key erforderlich' : '· Kein Key nötig'}</div>
      {p.erfordert_key && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {showKeyInput ? (
            <>
              <input type="password" placeholder="API-Key eingeben" value={keyInput} onChange={e => setKeyInput(e.target.value)} style={{ ...inputStyle, flex: 1, margin: 0 }} autoFocus />
              <button onClick={() => { onSaveKey(p.id, keyInput); setKeyInput(''); setShowKeyInput(false); }} style={{ ...btnStyle, padding: '8px 14px' }}>Speichern</button>
              <button onClick={() => { setShowKeyInput(false); setKeyInput(''); }} style={{ padding: '8px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Abbrechen</button>
            </>
          ) : (
            <button onClick={() => setShowKeyInput(true)} style={{ padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>{p.api_key_vorhanden ? 'Key aktualisieren' : 'API-Key hinterlegen'}</button>
          )}
        </div>
      )}
    </div>
  );
}

function KiAnbieterTab() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  useEffect(() => { api.get('/admin/ai/providers').then(r => setProviders(r.data.providers || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  async function toggleProvider(id, aktiv) {
    try { await api.put(`/admin/ai/providers/${id}`, { aktiv: aktiv ? 0 : 1 }); setProviders(prev => prev.map(p => p.id === id ? { ...p, aktiv: aktiv ? 0 : 1 } : p)); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.error || 'Fehler' }); }
  }
  async function saveApiKey(id, key) {
    if (!key.trim()) { setMsg({ ok: false, text: 'API-Key darf nicht leer sein' }); return; }
    try { await api.post(`/admin/ai/providers/${id}/key`, { api_key: key }); setMsg({ ok: true, text: 'API-Key gespeichert (verschlüsselt)' }); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.error || 'Fehler beim Speichern' }); }
  }
  if (loading) return <p>Lade Anbieter…</p>;
  return (
    <div>
      <h2 style={{ marginBottom: '8px' }}>KI-Anbieter</h2>
      <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '14px' }}>API-Keys werden verschlüsselt in der Datenbank gespeichert — nie im Klartext.</p>
      <Msg msg={msg} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {providers.map(p => <ProviderCard key={p.id} provider={p} onToggle={toggleProvider} onSaveKey={saveApiKey} />)}
      </div>
    </div>
  );
}

// ============================================================
// Tab: KI-Einstellungen (bestehend)
// ============================================================
function KiEinstellungenTab() {
  const [settings, setSettings] = useState(null);
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    Promise.all([api.get('/admin/ai/settings'), api.get('/admin/ai/providers')])
      .then(([s, p]) => { setSettings(s.data.settings || {}); setProviders((p.data.providers || []).filter(x => x.aktiv)); })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!settings?.active_provider_id) return;
    api.get(`/admin/ai/providers/${settings.active_provider_id}/models`).then(r => setModels(r.data.models || [])).catch(() => setModels([]));
  }, [settings?.active_provider_id]);
  async function saveSetting(key, value) {
    try { await api.put('/admin/ai/settings', { [key]: value }); setSettings(prev => ({ ...prev, [key]: value })); setMsg({ ok: true, text: 'Einstellung gespeichert' }); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.error || 'Fehler' }); }
  }
  if (!settings) return <p>Lade Einstellungen…</p>;
  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>KI-Einstellungen</h2>
      <Msg msg={msg} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '480px' }}>
        <div>
          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Aktiver KI-Anbieter</label>
          <select value={settings.active_provider_id || ''} onChange={e => saveSetting('active_provider_id', parseInt(e.target.value) || null)} style={inputStyle}>
            <option value="">Keiner (KI deaktiviert)</option>
            {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {models.length > 0 && (
          <div>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Aktives Modell</label>
            <select value={settings.active_model || ''} onChange={e => saveSetting('active_model', e.target.value)} style={inputStyle}>
              <option value="">Standard</option>
              {models.map(m => <option key={m.id} value={m.model_id}>{m.name} ({m.model_id})</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Tab: KI-Nutzung
// ============================================================
function KiNutzungTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/v2/ai/usage/stats').then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Lade KI-Nutzungsstatistiken…</p>;
  if (!stats) return <p>Keine Daten verfügbar.</p>;

  const totals = stats.totals || {};

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>KI-Nutzung & Kosten</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Anfragen gesamt', value: totals.anfragen || 0 },
          { label: 'Input-Tokens', value: (totals.prompt_tokens || 0).toLocaleString('de-DE') },
          { label: 'Output-Tokens', value: (totals.completion_tokens || 0).toLocaleString('de-DE') },
          { label: 'Fehlerrate', value: totals.anfragen ? `${Math.round((totals.fehler || 0) / totals.anfragen * 100)}%` : '-' },
        ].map(({ label, value }) => (
          <div key={label} style={{ ...cardStyle, textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#6366f1' }}>{value}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={cardStyle}>
        <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Letzte Anfragen</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Zeitpunkt', 'Funktion', 'Modell', 'Input', 'Output', 'Latenz', 'Status'].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(stats.recent || []).slice(0, 20).map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px', color: '#64748b' }}>{e.created_at ? new Date(e.created_at).toLocaleString('de-DE') : '-'}</td>
                  <td style={{ padding: '8px' }}>{e.funktion || '-'}</td>
                  <td style={{ padding: '8px', fontSize: '12px', color: '#94a3b8' }}>{(e.model_id || '-').slice(0, 25)}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{e.prompt_tokens || 0}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{e.completion_tokens || 0}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{e.latency_ms ? `${e.latency_ms}ms` : '-'}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: e.status === 'ok' ? '#dcfce7' : '#fee2e2', color: e.status === 'ok' ? '#16a34a' : '#dc2626' }}>{e.status || '-'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab: Systemeinstellungen
// ============================================================
function EinstellungenTab() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/v2/settings').then(r => setSettings(r.data.settings || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(key, value) {
    try {
      await api.put('/admin/v2/settings', { [key]: value });
      setMsg({ ok: true, text: `${key} gespeichert` });
      load();
      setEdits(e => ({ ...e, [key]: undefined }));
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || 'Fehler' });
    }
  }

  if (loading) return <p>Lade Einstellungen…</p>;

  const categories = [...new Set(settings.map(s => s.category))];

  return (
    <div>
      <h2 style={{ marginBottom: '8px' }}>Systemeinstellungen</h2>
      <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>Änderungen wirken sofort. Backup- und Sicherheits-Einstellungen erst nach Neustart vollständig aktiv.</p>
      <Msg msg={msg} />
      {categories.map(cat => (
        <div key={cat} style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '8px' }}>{cat}</h3>
          <div style={{ ...cardStyle, padding: '0' }}>
            {settings.filter(s => s.category === cat).map((s, idx, arr) => {
              const val = edits[s.key] !== undefined ? edits[s.key] : (s.value || '');
              const changed = edits[s.key] !== undefined && edits[s.key] !== (s.value || '');
              const isReadonly = s.key === 'backup.restic_prune_allowed' || s.key === 'app.version';
              return (
                <div key={s.key} style={{ padding: '12px 16px', borderBottom: idx < arr.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '13px' }}>{s.key}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>{s.description}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text" value={val} disabled={isReadonly}
                      onChange={e => setEdits(ed => ({ ...ed, [s.key]: e.target.value }))}
                      style={{ ...inputStyle, width: '180px', fontSize: '13px', padding: '6px 10px', opacity: isReadonly ? 0.5 : 1 }}
                    />
                    {!isReadonly && changed && (
                      <button onClick={() => save(s.key, val)} style={{ ...btnStyle, padding: '6px 12px', fontSize: '12px' }}>Speichern</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Tab: Datenbank
// ============================================================
function DatenbankTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/v2/database/status').then(r => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runAction(action, label) {
    if (!window.confirm(`${label} ausführen?`)) return;
    setActionMsg(null);
    setActionLoading(true);
    try {
      const r = await api.post(`/admin/v2/database/${action}`);
      setActionMsg({ ok: true, text: `${label}: ${r.data.result || 'OK'}` });
      load();
    } catch (err) {
      setActionMsg({ ok: false, text: err.response?.data?.error || `${label} fehlgeschlagen` });
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return <p>Lade Datenbankstatus…</p>;
  if (!status) return <p>Status nicht verfügbar.</p>;

  const info = status.info || {};
  const tableRows = status.tables || [];

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Datenbank-Administration</h2>
        <button onClick={load} style={{ padding: '6px 14px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', background: '#f8fafc' }}>Aktualisieren</button>
      </div>
      <Msg msg={actionMsg} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Größe', value: info.size_kb ? `${Math.round(info.size_kb)} KB` : '-' },
          { label: 'WAL-Größe', value: info.wal_size_kb ? `${Math.round(info.wal_size_kb)} KB` : '-' },
          { label: 'Integrity', value: info.integrity_ok ? '✓ OK' : '✗ Fehler' },
          { label: 'Tabellen', value: tableRows.length },
          { label: 'WAL-Modus', value: info.wal_mode || '-' },
          { label: 'Page Size', value: info.page_size || '-' },
        ].map(({ label, value }) => (
          <div key={label} style={{ ...cardStyle, textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#6366f1' }}>{value}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {[
          ['integrity-check', 'Integrity-Check'],
          ['quick-check', 'Quick-Check'],
          ['checkpoint', 'WAL-Checkpoint'],
          ['analyze', 'Analyze'],
          ['vacuum', 'VACUUM'],
          ['backup-local', 'Lokales Backup'],
        ].map(([action, label]) => (
          <button key={action} disabled={actionLoading} onClick={() => runAction(action, label)}
            style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', background: '#f8fafc' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={cardStyle}>
        <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Tabellen ({tableRows.length})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Tabelle', 'Zeilen', 'Größe (KB)'].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((t, i) => (
                <tr key={t.name} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px', fontFamily: 'monospace' }}>{t.name}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{t.count ?? '-'}</td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>{t.size_kb ? Math.round(t.size_kb) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Tab: Backup-Status
// ============================================================
function BackupTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/v2/backup/status').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Lade Backup-Status…</p>;
  if (!data) return <p>Backup-Status nicht verfügbar.</p>;

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>Backup-Status</h2>
      <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#92400e', fontSize: '14px' }}>
        <strong>Sicherheitshinweis:</strong> ALLOW_RESTIC_PRUNE ist deaktiviert. Keine Backup-Löschung möglich. Nur Lesen.
      </div>
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Restic-Status</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <tbody>
            {[
              ['ALLOW_RESTIC_PRUNE', data.allow_restic_prune ? 'Aktiv (GEFÄHRLICH)' : 'false (sicher)'],
              ['Restic verfügbar', data.restic_available ? 'Ja' : 'Nicht gefunden'],
              ['Repo verfügbar', data.repo_accessible ? 'Ja' : 'Nicht erreichbar'],
              ['Letztes Backup', data.last_backup || '-'],
              ['Snapshot-Anzahl', data.snapshot_count ?? '-'],
            ].map(([k, v]) => (
              <tr key={k} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px', color: '#64748b' }}>{k}</td>
                <td style={{ padding: '8px', fontWeight: '600' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.local_backups && data.local_backups.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ marginBottom: '12px', fontSize: '15px' }}>Lokale Backups</h3>
          {data.local_backups.map((b, i) => (
            <div key={i} style={{ padding: '8px', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
              {b.name} ({b.size_kb ? `${Math.round(b.size_kb)} KB` : '?'}) — {b.created_at || '-'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Tab: Audit-Log
// ============================================================
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', von: '', bis: '', success: '' });
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit, offset, ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) });
    api.get(`/admin/v2/audit?${params}`)
      .then(r => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [filter, offset]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <h2 style={{ marginBottom: '16px' }}>Audit-Log</h2>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <select style={{ ...inputStyle, width: 'auto' }} value={filter.category} onChange={e => { setFilter(f => ({ ...f, category: e.target.value })); setOffset(0); }}>
          <option value="">Alle Kategorien</option>
          {['system', 'auth', 'berichte', 'benutzerverwaltung', 'einstellungen', 'datenbank'].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" style={{ ...inputStyle, width: '140px' }} value={filter.von} onChange={e => { setFilter(f => ({ ...f, von: e.target.value })); setOffset(0); }} placeholder="Von" />
        <input type="date" style={{ ...inputStyle, width: '140px' }} value={filter.bis} onChange={e => { setFilter(f => ({ ...f, bis: e.target.value })); setOffset(0); }} placeholder="Bis" />
        <select style={{ ...inputStyle, width: 'auto' }} value={filter.success} onChange={e => { setFilter(f => ({ ...f, success: e.target.value })); setOffset(0); }}>
          <option value="">Alle</option>
          <option value="1">Erfolgreich</option>
          <option value="0">Fehlgeschlagen</option>
        </select>
        <button onClick={load} style={{ ...btnStyle, padding: '8px 14px' }}>Laden</button>
      </div>
      <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>{total} Einträge gesamt</p>
      {loading ? <p>Lade…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Zeitpunkt', 'Typ', 'Kategorie', 'Benutzer', 'IP', 'Ziel', 'Erfolg'].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: '#64748b' }}>{r.created_at ? new Date(r.created_at).toLocaleString('de-DE') : '-'}</td>
                  <td style={{ padding: '6px 8px' }}>{r.event_type}</td>
                  <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{r.category}</td>
                  <td style={{ padding: '6px 8px' }}>{r.username || '-'}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>{r.ip || '-'}</td>
                  <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{r.target_type ? `${r.target_type}:${r.target_id || ''}` : '-'}</td>
                  <td style={{ padding: '6px 8px' }}><span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: r.success ? '#dcfce7' : '#fee2e2', color: r.success ? '#16a34a' : '#dc2626' }}>{r.success ? 'OK' : 'Fehler'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center' }}>
            <button disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Zurück</button>
            <span style={{ fontSize: '13px', color: '#64748b' }}>{offset + 1}–{Math.min(offset + limit, total)} von {total}</span>
            <button disabled={offset + limit >= total} onClick={() => setOffset(o => o + limit)} style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Weiter</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Haupt-Komponente
// ============================================================
const adminTabs = [
  { key: 'uebersicht', label: 'Übersicht' },
  { key: 'benutzer', label: 'Benutzer' },
  { key: 'rollen', label: 'Rollen' },
  { key: 'sicherheit', label: 'Sicherheit' },
  { key: 'ki-anbieter', label: 'KI-Anbieter' },
  { key: 'ki-einstellungen', label: 'KI-Einstellungen' },
  { key: 'ki-nutzung', label: 'KI-Nutzung' },
  { key: 'einstellungen', label: 'Einstellungen' },
  { key: 'datenbank', label: 'Datenbank' },
  { key: 'backup', label: 'Backup-Status' },
  { key: 'audit', label: 'Audit-Log' },
];

export default function AdminPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') || 'uebersicht';
  const firstLogin = params.get('first') === '1';

  function setTab(key) { setParams({ tab: key }); }

  return (
    <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '2px', marginBottom: '24px', borderBottom: '2px solid #e2e8f0', paddingBottom: '0', flexWrap: 'wrap' }}>
        {adminTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '8px 14px', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid #6366f1' : '2px solid transparent', marginBottom: '-2px', cursor: 'pointer', fontWeight: tab === t.key ? '700' : '400', color: tab === t.key ? '#6366f1' : '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'uebersicht' && <UebersichtTab />}
      {tab === 'benutzer' && <BenutzerTab />}
      {tab === 'rollen' && <RollenTab />}
      {tab === 'sicherheit' && <SicherheitTab firstLogin={firstLogin} />}
      {tab === 'ki-anbieter' && <KiAnbieterTab />}
      {tab === 'ki-einstellungen' && <KiEinstellungenTab />}
      {tab === 'ki-nutzung' && <KiNutzungTab />}
      {tab === 'einstellungen' && <EinstellungenTab />}
      {tab === 'datenbank' && <DatenbankTab />}
      {tab === 'backup' && <BackupTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}
